import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { setMessage } from "./message";
import HandleError from "../HandleError";
import HandleResponse from "../HandleResponse";
import { contentErrorMessage } from "./contentError";
import AuthService from "../../services/auth.service";
import userApiController from "../../services/user.service";
import { devBypassUser, devLoginBypassEnabled } from "../../devLoginBypass";
import { readAuthToken } from "../../services/authContract";

const authErrorMessage = (action, fallback) => contentErrorMessage(action, fallback);

export const login = createAsyncThunk("auth/login", async (formvalues, thunkAPI) => {
  try {
    const authRequest = formvalues && formvalues.challenge_id
      ? AuthService.verifyTwoFactor(formvalues)
      : AuthService.login(formvalues, thunkAPI);
    const resposedata = await authRequest
      .then((response) => HandleResponse(thunkAPI, response, "login"))
      .catch((error) => HandleError(thunkAPI, error, "login"));
    const authToken = resposedata && resposedata.payload ? readAuthToken(resposedata.payload) : "";
    if (resposedata && resposedata.payload && resposedata.payload !== undefined && !resposedata.payload.status && authToken) {
      const user = await AuthService.getUser({ auth_key: resposedata.payload.auth_key || "", token: authToken })
        .then((response) => HandleResponse(thunkAPI, response, "getuser"))
        .catch((error) => HandleError(thunkAPI, error, "getuser"));

      if (user && user.payload && user.payload !== undefined && !user.payload.status) {
        return thunkAPI.fulfillWithValue({ isLoggedIn: true, user: user.payload, token: authToken, navigate: resposedata.payload.navigate });
      } else {
        return user;
      }
    }
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    thunkAPI.dispatch(setMessage(message));
    return thunkAPI.rejectWithValue({ status: error.response.status, message: message });
  }
});

export const redeemAdminHandoff = createAsyncThunk("auth/redeemAdminHandoff", async (grant, thunkAPI) => {
  try {
    const response = await AuthService.redeemAdminHandoff(grant);
    const responsePayload = response && (response.data || response.payload) || {};
    const authToken = readAuthToken(responsePayload);
    if (authToken) {
      const userResponse = await AuthService.getUser({ auth_key: responsePayload.auth_key || "", token: authToken });
      const userPayload = userResponse && (userResponse.data || userResponse.payload) || {};
      if (userPayload && !userPayload.status) {
        return thunkAPI.fulfillWithValue({
          isLoggedIn: true,
          user: userPayload,
          token: authToken,
          navigate: responsePayload.navigate || "calendar",
        });
      }
      return thunkAPI.rejectWithValue({ status: 502, message: "Unable to load the account profile." });
    }
    return thunkAPI.rejectWithValue({ status: 422, message: "The account handoff did not return a session." });
  } catch (error) {
    return HandleError(thunkAPI, error, "admin_handoff");
  }
});

export const TwoFactorLoginApi = createAsyncThunk("auth/twofactor", async (formValues, thunkAPI) => {
  try {
    const resposedata = await AuthService
      .twofactorauthentications(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "user"))
      .catch((error) => HandleError(thunkAPI, error, "user"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const SalonSelectionApi = createAsyncThunk("auth/salonselection", async (formValues, thunkAPI) => {
  try {
    const resposedata = await AuthService
      .salonselection(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "user"))
      .catch((error) => HandleError(thunkAPI, error, "user"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const Logout = createAsyncThunk("auth/logout", async (formvalues, thunkAPI) => {
  try {
    const resposedata = await AuthService.logout()
      .then((response) => HandleResponse(thunkAPI, response, "logout"))
      .catch((error) => HandleError(thunkAPI, error, "logout"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const GetUser = createAsyncThunk("auth/user", async (formValues, thunkAPI) => {
  try {
    const resposedata = await userApiController
      .getUser(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "user"))
      .catch((error) => HandleError(thunkAPI, error, "user"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const Forgotpassowrdsubmit = createAsyncThunk("auth/forgotpassowrdsubmit", async (formValues, thunkAPI) => {
  try {
    const resposedata = await AuthService.forgotpassowrdsubmit(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "suggetionlist"))
      .catch((error) => HandleError(thunkAPI, error, "suggetionlist"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const ForgotPasswordSalonApi = createAsyncThunk("forgotpassword/select", async (formValues, thunkAPI) => {
  try {
    const resposedata = await AuthService
      .forgotPasswordSalonSelect(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "forgotpasswordselect"))
      .catch((error) => HandleError(thunkAPI, error, "forgotpasswordselect"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

const initialState = {
  token: devLoginBypassEnabled ? "dev-bypass-token" : "",
  navigate: "",
  isLoggedIn: devLoginBypassEnabled,
  isInitialized: devLoginBypassEnabled,
  isOpenAdminSalonModal: false,
  isOpenSalonSelectionPopup: "",
  user: devLoginBypassEnabled ? devBypassUser : null,
  admindata: null,
  isAdminSalonFormValues: null,
  isOpenTwoFactorAuthentications: "",
  isSalonOption: [],
  isLoginRelatedData: "",
  isSalonSelectedMetaData: "",
  isForgotPasswordSalonData: [],
  isOpenForgotPasswordSalonSelect: "",
  isForgotpasswordSalonSelectionData: "",
  salonSelectionError: "",
  isApiStatus: {
    login: "",
    GetUser: "",
    Forgotpassowrdsubmit: "",
    TwoFactorLoginApi: "",
    SalonSelectionApi: "",
    ForgotPasswordSalonApi: "",
  },
};

const applyDevLoginBypass = (state) => {
  if (!devLoginBypassEnabled) {
    return;
  }
  state.token = "dev-bypass-token";
  state.isLoggedIn = true;
  state.isInitialized = true;
  state.user = devBypassUser;
  state.navigate = "";
};

const clearAuthSession = (state) => {
  state.isLoggedIn = false;
  state.token = "";
  state.user = null;
  state.admindata = null;
  state.isAdminSalonFormValues = null;
  applyDevLoginBypass(state);
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    HydrateAuthUser: (state, action) => {
      state.user = {
        ...state.user,
        ...action.payload,
      };
    },
    authlogout: (state) => {
      clearAuthSession(state);
    },
    OpenAdminSalonModal: (state, action) => {
      state.isOpenAdminSalonModal = action.payload;
    },
    AdminSalonFormValues: (state, action) => {
      state.isAdminSalonFormValues = action.payload;
    },
    AdminData: (state, action) => {
      state.admindata = action.payload;
    },
    OpenTwoFactorAuthentications: (state, action) => {
      state.isOpenTwoFactorAuthentications = action.payload;
    },
    TwoFactorAuthenticationsData: (state, action) => {
      state.isLoginRelatedData = action.payload;
    },
    OpenSalonSelectionPopup: (state, action) => {
      state.isOpenSalonSelectionPopup = action.payload;
    },
    SalonSelectedMetaData: (state, action) => {
      state.isSalonSelectedMetaData = action.payload;
    },
    OpenForgotPasswordSalonSelect: (state, action) => {
      state.isOpenForgotPasswordSalonSelect = action.payload;
    },
    ForgotpasswordSalonSelectionData: (state, action) => {
      state.isForgotpasswordSalonSelectionData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase("persist/REHYDRATE", (state, action) => {
        if (action.key === "auth") {
          applyDevLoginBypass(state);
        }
      })
      .addCase(login.fulfilled, (state, action) => {
        if (!action.payload || !action.payload.isLoggedIn) {
          clearAuthSession(state);
          return;
        }
        state.isLoggedIn = true;
        state.navigate = action.payload.navigate;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.isInitialized = true;
      })
      .addCase(redeemAdminHandoff.fulfilled, (state, action) => {
        if (!action.payload || !action.payload.isLoggedIn) return;
        state.isLoggedIn = true;
        state.navigate = action.payload.navigate || "calendar";
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.isInitialized = true;
      })
      .addCase(redeemAdminHandoff.rejected, (state) => {
        state.isLoggedIn = false;
        state.token = "";
        state.user = null;
        applyDevLoginBypass(state);
      })
      .addCase(login.rejected, (state, action) => {
        // A throttled auth request does not revoke an already valid session.
        if (action.payload?.status === 429) return;
        state.isLoggedIn = false;
        state.token = "";
        state.user = null;
        applyDevLoginBypass(state);
        const status = action.payload && action.payload.status;
        const response = action.payload && action.payload.message && action.payload.message;
        if (status === 410) {
          const users = response && response.users ? response.users : "";
          const adminEmail = response && response.email ? response.email : "";
          if (users && adminEmail) {
            state.admindata = response;
          }
        }
      })
      .addCase(Logout.fulfilled, (state) => {
        state.isLoggedIn = false;
        state.token = "";
        state.user = null;
        state.admindata = null;
        state.isAdminSalonFormValues = null;
        applyDevLoginBypass(state);
      })
      .addCase(Logout.rejected, (state) => {
        state.isLoggedIn = false;
        state.token = "";
        state.user = null;
        state.admindata = null;
        state.isAdminSalonFormValues = null;
        applyDevLoginBypass(state);
      })
      .addCase(GetUser.pending, () => { })
      .addCase(GetUser.fulfilled, (state, action) => {
        if (devLoginBypassEnabled) {
          applyDevLoginBypass(state);
          return;
        }
        state.user = action.payload;
      })
      .addCase(GetUser.rejected, (state) => {
        applyDevLoginBypass(state);
      })
      .addCase(Forgotpassowrdsubmit.pending, () => { })
      .addCase(Forgotpassowrdsubmit.fulfilled, () => { })
      .addCase(Forgotpassowrdsubmit.rejected, () => { })
      .addCase(TwoFactorLoginApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, TwoFactorLoginApi: "loading" };
      })
      .addCase(TwoFactorLoginApi.fulfilled, (state) => {
        state.isLoggedIn = false;
        state.isApiStatus = { ...state.isApiStatus, TwoFactorLoginApi: "succeeded" };
      })
      .addCase(TwoFactorLoginApi.rejected, (state) => {
        state.isApiStatus = { ...state.isApiStatus, TwoFactorLoginApi: "failed" };
      })
      .addCase(SalonSelectionApi.pending, (state) => {
        state.salonSelectionError = "";
        state.isApiStatus = { ...state.isApiStatus, SalonSelectionApi: "loading" };
      })
      .addCase(SalonSelectionApi.fulfilled, (state, action) => {
        state.isLoggedIn = false;
        state.isSalonOption = action.payload;
        state.salonSelectionError = "";
        state.isApiStatus = { ...state.isApiStatus, SalonSelectionApi: "succeeded" };
      })
      .addCase(SalonSelectionApi.rejected, (state, action) => {
        state.salonSelectionError = authErrorMessage(action, "Unable to load salons.");
        state.isApiStatus = { ...state.isApiStatus, SalonSelectionApi: "failed" };
      })
      .addCase(ForgotPasswordSalonApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, ForgotPasswordSalonApi: "loading" };
      })
      .addCase(ForgotPasswordSalonApi.fulfilled, (state, action) => {
        state.isForgotPasswordSalonData = action.payload;
        state.isApiStatus = { ...state.isApiStatus, ForgotPasswordSalonApi: "succeeded" };
      })
      .addCase(ForgotPasswordSalonApi.rejected, (state) => {
        state.isForgotPasswordSalonData = [];
        state.isApiStatus = { ...state.isApiStatus, ForgotPasswordSalonApi: "failed" };
      });
  },
});

export const { HydrateAuthUser, authlogout, OpenAdminSalonModal, AdminSalonFormValues, AdminData, OpenTwoFactorAuthentications, TwoFactorAuthenticationsData, OpenSalonSelectionPopup, SalonSelectedMetaData, OpenForgotPasswordSalonSelect, ForgotpasswordSalonSelectionData } = authSlice.actions;
export default authSlice.reducer;
